import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import SessionManagement from "./SessionManagement";
import ClassManagement from "./ClassManagement";
import SectionsManagement from "./SectionsManagement";
import SectionAssignment from "./SessionAssignment";
import StudentList from "./StudentList";
import CampusListView from "./CampusListView";
import CampusDetailView from "./CampusDetailView";
import {
  API_BASE_URL,
  getAuthHeaders,
  apiErrorMessage,
  SERVER_UNAVAILABLE_MESSAGE,
} from "../../../utils/api";
import StaffManagement from "./StaffManagement";

/**
 * CampusDashboardRouter - Routes the user to the correct component based on URL
 */
function CampusDashboardRouter({ onLogout, showToast, analyticsOnly = false }) {
  const [campuses, setCampuses] = useState([]);
  const [principals, setPrincipals] = useState([]);
  const [loadError, setLoadError] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const { campusId, classId } = useParams();
  const isSuperadmin =
    (localStorage.getItem("als_role") || "").toLowerCase() === "superadmin";

  // Load data
  useEffect(() => {
    const loadCampuses = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/campuses`, {
          headers: getAuthHeaders(),
        });
        if (res.status === 401) {
          onLogout?.();
          return;
        }
        if (!res.ok) {
          throw new Error(apiErrorMessage(res, "Failed to load campuses"));
        }
        const data = await res.json();
        setCampuses(Array.isArray(data) ? data : []);
        setLoadError("");
      } catch (error) {
        console.error("Failed to load campuses", error);
        const message =
          String(error?.message || "").includes("Failed to fetch")
            ? SERVER_UNAVAILABLE_MESSAGE
            : error?.message || SERVER_UNAVAILABLE_MESSAGE;
        setLoadError(message);
        showToast?.("error", "Load Error", message);
      }
    };

    const loadPrincipals = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/principals`, {
          headers: getAuthHeaders(),
        });
        if (res.status === 401) {
          onLogout?.();
          return;
        }
        if (!res.ok) throw new Error("Failed to load principals");
        const data = await res.json();
        setPrincipals(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Failed to load principals", error);
        showToast?.("error", "Load Error", "Could not load principal list.");
        setPrincipals([]);
      }
    };

    loadCampuses();
    if (isSuperadmin) {
      loadPrincipals();
    }
  }, [isSuperadmin, onLogout, showToast]);

  const getCampusName = (id) => {
    const campus = campuses.find((c) => String(c.id) === String(id));
    return campus ? campus.name : "Campus";
  };

  const pathname = location.pathname;

  // Log for debugging
  useEffect(() => {
    console.log("=== CampusDashboardRouter UPDATED ===");
    console.log("Full pathname:", pathname);
    console.log("campusId from params:", campusId);
    console.log("classId from params:", classId);
  }, [pathname, campusId, classId]);

  // Determine which component to render based on URL
  const hasClassesInPath = pathname.includes("/classes");
  const hasSessionsInPath = pathname.includes("/sessions");
  const hasSectionsInPath = pathname.includes("/sections");
  const hasAssignmentsInPath =
    pathname.includes("/session-assignments") ||
    pathname.includes("/section-assignments");
  const hasStaffInPath = pathname.includes("/staff");

  useEffect(() => {
    console.log("Path Analysis:");
    console.log("- hasClassesInPath:", hasClassesInPath);
    console.log("- hasSessionsInPath:", hasSessionsInPath);
    console.log("- hasSectionsInPath:", hasSectionsInPath);
    console.log("- hasAssignmentsInPath:", hasAssignmentsInPath);
    console.log("- hasStaffInPath:", hasStaffInPath);
    console.log("- campusId exists:", !!campusId);
    console.log("- classId exists:", !!classId);
  }, [
    pathname,
    campusId,
    classId,
    hasClassesInPath,
    hasSessionsInPath,
    hasSectionsInPath,
    hasAssignmentsInPath,
    hasStaffInPath,
  ]);

  if (analyticsOnly) {
    return (
      <CampusListView
        key="campus-analytics-list"
        campuses={campuses}
        setCampuses={setCampuses}
        principals={principals}
        showToast={showToast}
        onSelectCampus={() => {}}
        onLogout={onLogout}
        analyticsOnly
      />
    );
  }

  // Route: Student List under a class
  if (classId && campusId) {
    console.log("✅ ROUTE MATCH: StudentList");
    return (
      <StudentList
        key={`student-${classId}`}
        campusId={campusId}
        classId={classId}
        onBack={() => navigate(`/superadmin/campuses/${campusId}/classes`)}
        showToast={showToast}
        onLogout={onLogout}
      />
    );
  }

  // Route: Classes Management
  if (campusId && hasClassesInPath && !hasSessionsInPath) {
    console.log("✅ ROUTE MATCH: ClassManagement");
    return (
      <ClassManagement
        key={`classes-${campusId}`}
        campusId={campusId}
        campusName={getCampusName(campusId)}
        onBack={() => navigate(`/superadmin/campuses/${campusId}`)}
        showToast={showToast}
        onLogout={onLogout}
      />
    );
  }

  // Route: Create Sections
  if (campusId && hasSectionsInPath) {
    console.log("ROUTE MATCH: SectionsManagement");
    return <SectionsManagement key={`sections-${campusId}`} />;
  }

  // Route: Section Assignment
  if (campusId && hasAssignmentsInPath) {
    console.log("ROUTE MATCH: SectionAssignment");
    return (
      <SectionAssignment
        key={`assignments-${campusId}`}
        campusId={campusId}
        campusName={getCampusName(campusId)}
        onBack={() => navigate(`/superadmin/campuses/${campusId}`)}
        showToast={showToast}
        onLogout={onLogout}
      />
    );
  }

  // Route: Sessions Management
  if (campusId && hasSessionsInPath) {
    console.log("✅ ROUTE MATCH: SessionManagement");
    return (
      <SessionManagement
        key={`sessions-${campusId}`}
        campusId={campusId}
        campusName={getCampusName(campusId)}
        onBack={() => navigate(`/superadmin/campuses/${campusId}`)}
        showToast={showToast}
        onLogout={onLogout}
      />
    );
  }

  // Route: Staff Management - THIS MUST COME BEFORE CampusDetailView
  if (campusId && hasStaffInPath) {
    console.log("✅ ROUTE MATCH: StaffManagement");
    return (
      <StaffManagement
        key={`staff-${campusId}`}
        campusId={campusId}
        campusName={getCampusName(campusId)}
        onBack={() => navigate(`/superadmin/campuses/${campusId}`)}
        showToast={showToast}
        onLogout={onLogout}
      />
    );
  }

  // Route: Campus Detail View (2 Cards)
  if (
    campusId &&
    !hasClassesInPath &&
    !hasSessionsInPath &&
    !hasSectionsInPath &&
    !hasAssignmentsInPath &&
    !hasStaffInPath
  ) {
    console.log("✅ ROUTE MATCH: CampusDetailView");
    return (
      <CampusDetailView
        key={`detail-${campusId}`}
        campusId={campusId}
        campusName={getCampusName(campusId)}
        onBack={() => navigate("/superadmin/campuses")}
        onLogout={onLogout}
        showToast={showToast}
      />
    );
  }

  // Route: Campus List View
  console.log("✅ ROUTE MATCH: CampusListView");
  return (
    <>
      {loadError ? (
        <div className="viewer-empty-state" style={{ margin: "16px 24px 0" }}>
          <h3>Could not load campuses</h3>
          <p>{loadError}</p>
        </div>
      ) : null}
      <CampusListView
        key="campus-list"
        campuses={campuses}
        setCampuses={setCampuses}
        principals={principals}
        showToast={showToast}
        onSelectCampus={(id) => navigate(`/superadmin/campuses/${id?.id ?? id}`)}
        onLogout={onLogout}
      />
    </>
  );
}

export default CampusDashboardRouter;